import React from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmDialog } from '@/components/deleteConfirmDialog';
import { ErrorBoundary } from '@/components/errorBoundary';
import { BrandFormModal } from './components';
import { BrandsPageSkeleton } from './BrandsPageSkeleton';
import { BrandsPageView } from './BrandsPageView';
import { useBrandsPage } from './useBrandsPage';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/**
 * /brands page — the brand-management view. Owns its data hook (`useBrandsPage`)
 * and an `ErrorBoundary`; the throw lives in the inner Content child because
 * React boundaries don't catch errors thrown by the component that renders
 * them. The form modal renders outside the boundary so a grid render failure
 * can't take it down.
 */
export const BrandsPage: React.FC = () => {
    const page = useBrandsPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('brands.components.brandTable.errorTitle'),
            errorMessage: t('brands.components.brandTable.errorMessage'),
            errorRetry: t('brands.components.brandTable.errorRetry'),
        }),
        [t],
    );

    return (
        <div
            className="mx-auto flex w-full max-w-7xl flex-col gap-8"
            {...getNodeTestIdProp('brands-page')}
            id="brands-page"
        >
            <ErrorBoundary
                data-testid="brands-page-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="brands-page-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <BrandsPageContent page={page} />
            </ErrorBoundary>
            <DeleteConfirmDialog
                confirm={page.deleteConfirm}
                descriptionKey="deleteConfirm.brandDescription"
                promptKey="deleteConfirm.brandPrompt"
            />
            <BrandFormModal
                error={page.formError}
                initialValues={page.editingInitialValues}
                isOpen={page.isFormOpen}
                isSubmitting={page.isSubmitting}
                mode={page.formMode}
                onClose={page.onCloseForm}
                onSubmit={page.onSubmitBrand}
            />
        </div>
    );
};

type BrandsPageContentProps = {
    page: ReturnType<typeof useBrandsPage>;
};

const BrandsPageContent: React.FC<BrandsPageContentProps> = ({ page }) => {
    if (page.error) {
        throw page.error;
    }

    if (page.isLoading || page.data === null) {
        return <BrandsPageSkeleton />;
    }

    return (
        <BrandsPageView
            data={page.data}
            onAddBrand={page.onAddBrand}
            onDeleteBrand={page.onDeleteBrand}
            onEditBrand={page.onEditBrand}
            onPageChange={page.onPageChange}
            onPageSizeChange={page.onPageSizeChange}
            onSearchChange={page.onSearchChange}
            page={page.page}
            pageSize={page.pageSize}
            search={page.search}
            totalCount={page.totalCount}
            totalPages={page.totalPages}
        />
    );
};
